package com.dynamicruntime.webapp

import com.dynamicruntime.common.gedra.branchDisplayName
import com.dynamicruntime.common.cfact.CFactParser
import com.dynamicruntime.common.gedra.GE
import com.dynamicruntime.common.schema.PRES
import com.dynamicruntime.common.schema.PSTAT
import com.dynamicruntime.common.schema.SCH
import com.dynamicruntime.common.schema.SCT
import com.dynamicruntime.common.schema.SchFailure
import com.dynamicruntime.common.schema.SchOption
import com.dynamicruntime.common.schema.SchLayout
import com.dynamicruntime.common.schema.SchLayoutField
import com.dynamicruntime.common.schema.errorContextData
import com.dynamicruntime.common.schema.SchProperty
import com.dynamicruntime.common.schema.SchType
import com.dynamicruntime.common.schema.SchVariants
import com.dynamicruntime.common.schema.SLDM
import com.dynamicruntime.common.schema.boundsContextData
import com.dynamicruntime.common.schema.byPath
import com.dynamicruntime.common.schema.childKeyOf
import com.dynamicruntime.common.schema.childPath
import com.dynamicruntime.common.schema.indexPath
import com.dynamicruntime.common.schema.isBinaryFormat
import com.dynamicruntime.common.schema.isDateFormat
import com.dynamicruntime.common.schema.isPathAtOrBelow
import com.dynamicruntime.common.schema.orderedFieldNames
import com.dynamicruntime.common.util.analyzeTemplate
import com.dynamicruntime.common.util.evalTemplate
import com.dynamicruntime.common.util.fmtD
import com.dynamicruntime.common.util.toJsonStr
import com.dynamicruntime.common.util.toOptStr
import kotlinx.browser.document
import web.dom.ElementId
import org.w3c.dom.HTMLElement
import react.ChildrenBuilder
import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.pre
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.table
import react.dom.html.ReactHTML.tbody
import react.dom.html.ReactHTML.td
import react.dom.html.ReactHTML.textarea
import react.dom.html.ReactHTML.th
import react.dom.html.ReactHTML.thead
import react.dom.html.ReactHTML.tr
import react.use
import react.useEffect
import web.cssom.ClassName
import com.dynamicruntime.common.util.toJsonMapOrEmpty
import com.dynamicruntime.common.util.toJsonListOrEmpty
import com.dynamicruntime.common.util.humanizeFieldName

/**
 * How the form renders, beyond the schema itself (issue #408): whether it is a **friendly** data-entry form or
 * the wire-documenting catalog view, and any root fields to omit. The defaults reproduce the catalog's
 * behavior exactly, so a caller that passes nothing is unchanged.
 */
class FormOpts(
    /**
     * Friendly mode: label a field with its schema `title`, or a humanized key when it declares none, and
     * **hide** derived fields entirely rather than showing them read-only. The catalog wants the opposite --
     * it documents the wire, so it keeps the key and shows the derived field it is documenting.
     */
    val friendly: Boolean = false,
    /**
     * Root-level field names to leave out of the form entirely -- an advanced flag an end-user form should not
     * offer. Applies only at the top level, so a same-named field deeper in the tree is untouched.
     */
    val omit: Set<String> = emptySet(),
    /**
     * A property's `g-visibleWhen` gate (issue #564): given the expression, whether the field is shown.
     * Consulted only for an *editable* field -- a read-only render (the catalog documenting the wire, a stored
     * form on display) shows every field. Defaults to always-visible, so a form that supplies no cfacts shows
     * everything; the gate is presentation-only and the backend enforces the condition regardless.
     */
    val gateAllows: (expression: String) -> Boolean = { true },
    /**
     * The per-type field layouts (issue #586), keyed by qualified type name. Consulted only in [friendly] mode, and
     * only for the copy overrides (label / description); empty means every field keeps its schema `title` /
     * `description`, which is what the catalog and every read-only-of-the-wire caller wants.
     */
    val fieldLayouts: Map<String, SchLayout> = emptyMap(),
    /**
     * Promote a keyed object property's primary-key fields to the object's own level (issue #642); see
     * [SchemaFormProps.promoteKeys]. Off by default, so only the form that opts in reshapes.
     */
    val promoteKeys: Boolean = false,
    /**
     * How this form's **root** fields present a supplied default (issue #710), keyed by field name -- a `filled`
     * field to mark as a suggestion, or an `offer` field to fill only on a click. Consulted at the top level
     * only (like [omit]): the presenting decision is the trait's, and a same-named field one level down is a
     * different field. Empty means no field carries a default, which is every form until one is prefilled.
     */
    val prefill: Map<String, FieldPrefill> = emptyMap(),
    /**
     * Draw a trait union's discriminator ([GE.traitId]) as an open combobox rather than a closed dropdown
     * (issue #667), so a cross-client admin can enter a trait this node's global union does not list -- one
     * belonging to a client whose config it never loaded. Only the `traitId` discriminator opens; every other
     * union stays a closed choice. Typing an unknown trait lands on the union's open default branch, whose
     * `data` is a free-form object -- so the trait *and* its data can be entered by hand. Off by default: only
     * the edit form, on the shared/global surface, sets it.
     */
    val openTraitEntry: Boolean = false,
    /**
     * Show a **derived** field read-only when it holds a value (issue #712), instead of hiding it as [friendly]
     * mode otherwise does. A derived *data* value (an expense report's total) is computed and stored by the backend
     * and is real content the person should see -- but never a control they fill, so it is only ever shown on a
     * **read-only** form and never drawn as an input. A derived field with no value stays hidden rather than
     * drawing a blank, and this changes nothing on an editable form (there is no control to offer).
     *
     * It reveals only derived **content**, never derived **envelope** -- a gedra's system stamps (its id, an
     * entry's `entryId`/`source`/audit) are `derived` too, and a raw document view must not surface them. Which
     * is which is decided structurally, by [derivedRootIsTraitData] and [traitDataField]: with neither set, this
     * reveals nothing, so a form that opts in must also say where its trait content lives.
     */
    val showDerivedValues: Boolean = false,
    /**
     * The whole form root is a trait's **data** payload (issue #712) -- the survey View Info, which renders one
     * trait's data type directly -- so every derived-with-value field at any depth is content, not envelope, and
     * [showDerivedValues] reveals it. The trait data carries no system envelope, so there is nothing to keep
     * hidden. Off by default.
     */
    val derivedRootIsTraitData: Boolean = false,
    /**
     * The property name whose object is a trait's **data** payload (issue #712) -- for a form rendering a whole
     * document (the raw read-only view over a `FormDoc`), where the trait content sits under `entries[].data`
     * and the surrounding document and entry fields are system envelope. A derived-with-value field at or below
     * a path segment of this name is revealed by [showDerivedValues]; one outside it (the document id, an
     * entry's audit stamps) stays hidden. Null when the root already is trait data ([derivedRootIsTraitData])
     * or nothing opts in.
     */
    val traitDataField: String? = null,
    /**
     * A note to draw beside a list element's `[i]` header (issue #857): asked per element with its path and value,
     * and drawn when it answers one -- the raw editor's "Locked by Audit review" on a section the caller cannot
     * change -- so what the page knows about an element is said on the element, not only in a notice above the
     * form. Purely presentational; nothing about the element's editing changes.
     */
    val elementNote: (path: String, element: Map<String, Any?>) -> String? = { _, _ -> null },
    /**
     * Design View (issue #972): the session that marks this form's fields and draws the ones it hides as ghosts, or
     * null -- no Design View, and the form draws exactly as it otherwise would. Set from [DesignViewContext], which
     * only the workflow form provides, so no other surface is ever marked.
     */
    val design: DesignSession? = null,
) {
    /**
     * Whether a derived field at [path] holding [value] should be shown read-only rather than hidden (issue
     * #712). True only for a *content* derived value on a read-only form that opted in: [showDerivedValues] set,
     * not [editable], a non-blank value, and [path] inside trait data -- either because the whole form root is
     * trait data ([derivedRootIsTraitData]) or because a path segment names the [traitDataField] payload. A
     * derived envelope field (a gedra id, an entry's audit stamps) lies outside that boundary and stays hidden.
     */
    fun revealsDerivedAt(path: String, editable: Boolean, value: Any?): Boolean {
        if (!showDerivedValues || editable || isBlankValue(value)) {
            return false
        }
        val insideTraitData = derivedRootIsTraitData ||
            (traitDataField != null && path.split('.').any { it == traitDataField })
        return insideTraitData
    }
}

/**
 * How one **root** field presents its supplied default (issue #710), resolved by [WorkflowForm] from the view's
 * [PrefillPresentation] and the field's `defaultMode`. The two modes render differently:
 *  - **[SLDM.filled]** ([suggested] true) -- the value is already in the control; mark it a suggestion. Once the
 *    user touches the field it becomes theirs and [suggested] goes false; [onReset] (non-null only then) brings
 *    the suggestion back.
 *  - **[SLDM.offer]** -- the control is empty and [offeredValue] sits behind a "Use it" link; the link applies
 *    the value through the field's ordinary edit path, so an applied offer is indistinguishable from a typed one.
 */
class FieldPrefill(
    val mode: String,
    val offeredValue: Any? = null,
    val suggested: Boolean = false,
    val onReset: (() -> Unit)? = null,
)

/**
 * A field's resolved layout copy overrides (issues #586, #587): each is null when the layout does not override
 * it, so the render site falls back to the schema's `title` / `description` and the derived bound hint. The
 * [label] and [description] resolve against the field's own data; the [hint] resolves against the field's
 * **bounds context** (`${'$'}{min}` / `${'$'}{max}`), a different data set -- which is why they are resolved together
 * here, each against its own context, rather than at the render site.
 */
class LayoutCopy(
    val label: String?,
    val description: String?,
    val hint: String?,
    /**
     * The field's **error override** templates (issue #588), by `SchFailCode` name (plus `default`), left
     * **unresolved** here: an error message resolves against its *failure's* params (the offending value, the
     * option list, the bound), which are not known until a failure is in hand -- so [layoutErrorMessage]
     * resolves one per [SchFailure] at render time, unlike the copy above which resolves once per field.
     */
    val errors: Map<String, String> = emptyMap(),
    /** The layout's entry for the field itself, for what it says beyond copy: its form requirements (issue #1022). */
    val entry: SchLayoutField? = null,
)

/**
 * Resolve one layout template [text] against [data], **fail-safe** (issues #586, #587): plain copy (no `$`)
 * returns as written with no parse, and a `${'$'}{…}` the data cannot resolve (or a stray `${'$'}`) is logged with the
 * `[kdr]` console prefix and returned as written rather than blanking the field -- presentation should not hide
 * a field's copy over a missing substitution, and webapp/CLAUDE.md's rule is to never swallow without a signal.
 */
private fun resolveLayoutTemplate(text: String, data: Map<String, Any?>): String {
    if ('$' !in text) return text
    val report = text.analyzeTemplate(evaluateWith = data)
    return report.value ?: text.also {
        val why = report.issues.first().message
        console.warn("$errorLogPrefix could not resolve a layout template, showing it as written: \"$text\" ($why)")
    }
}

/**
 * The layout copy overrides for property [name] of object [type], for display (issues #586, #587), or null when
 * no layout for the type addresses the field. `label` / `description` resolve against the field's own [values];
 * `hint` resolves against the field's bounds context ([boundsContextData], `${'$'}{min}` / `${'$'}{max}`). Only in
 * friendly mode -- the wire-documenting view ignores layouts as it ignores `title`.
 */
internal fun layoutCopy(type: SchType, name: String, values: Map<String, Any?>, opts: FormOpts): LayoutCopy? {
    if (!opts.friendly) return null
    val field = type.name?.let { opts.fieldLayouts[it] }?.fieldFor(name) ?: return null
    // The bounds context is built only when there is a hint to resolve against it -- a field overriding just
    // its label/description (the common case) pays nothing for it.
    fun boundsData(): Map<String, Any?> = type.properties[name]?.let { boundsContextData(it.valueType) } ?: emptyMap()
    return LayoutCopy(
        label = field.label?.let { resolveLayoutTemplate(it, values) },
        description = field.description?.let { resolveLayoutTemplate(it, values) },
        hint = field.hint?.let { resolveLayoutTemplate(it, boundsData()) },
        // Kept unresolved -- resolved per failure by layoutErrorMessage, against that failure's params.
        errors = field.errors,
        entry = field,
    )
}

/**
 * The layout's wording for failure [f] on field [name] of type [prop] (issue #588), or null to leave the
 * framework's own message (or the `g-errors` floor) in place. The override is looked up by the failure's code,
 * then `default`, and resolved -- fail-safe, like all layout copy -- against the failure's params
 * ([errorContextData]: the offending [value], the option list, the bound), the same `${'$'}{…}` machinery the
 * templated hint uses. This is where message *variation* is homed, so it wins over the built-in wording.
 */
internal fun layoutErrorMessage(copy: LayoutCopy?, prop: SchProperty, name: String, value: Any?, f: SchFailure): String? {
    val template = copy?.errors?.let { it[f.code.name] ?: it[SCH.errorDefault] } ?: return null
    return resolveLayoutTemplate(template, errorContextData(f.code, prop.valueType, name, value, f.options))
}

/**
 * The label a field shows: its schema title (or a humanized key) in friendly mode, else the raw wire key. The
 * title is the **property's**, never its value type's -- a `$ref` field's value type is shared, so a title from
 * there would label every field referencing that type alike (see [SchProperty.title]).
 */
private fun fieldLabel(name: String, prop: SchProperty, opts: FormOpts): String =
    if (opts.friendly) prop.title ?: humanizeFieldName(name) else name

/**
 * The primary-key value(s) an array element carries, as a label, or null when the element's type is not keyed
 * (issue #487). This is what lets several entries of one keyed trait read as "2024", "2025" rather than a bare
 * index -- a gedra holds one `yearly` entry per year, told apart by that key.
 *
 * Deliberately generic: it knows nothing of traits, only that a type may declare a `g-primaryKey` (surfaced on
 * [SchType.primaryKey], Slice 1). It looks two ways -- the element's own type is keyed (a directly-keyed object
 * list), or one of its properties' types is (the trait case: the key lives on `data`'s type and its fields sit
 * one level down under `data`). For a union it first resolves which branch the element is. Pure, and covered
 * under `jsNodeTest`.
 */
fun keyedElementLabel(elementType: SchType, element: Map<String, Any?>): String? {
    val branch = elementType.variants?.let { it.select(element[it.discriminator] as? String) } ?: elementType
    directPrimaryKeyLabel(branch, element)?.let { return it }
    for ((propName, prop) in branch.properties) {
        directPrimaryKeyLabel(prop.valueType, element[propName])?.let { return it }
    }
    return null
}

/** [type]'s own primary-key values read out of [value] (the map keyed by [type]'s fields), or null if not keyed. */
private fun directPrimaryKeyLabel(type: SchType, value: Any?): String? {
    if (type.primaryKey.isEmpty()) return null
    val data = value as? Map<*, *> ?: return null
    val parts = type.primaryKey.map { data[it] }
    // Every key field must be present, or the label would read as complete while it is not: two rows of a
    // composite-keyed trait with only their first field filled in would both show that one value and collide.
    // An incomplete key yields no label, so the row falls back to its index until the key is whole.
    return if (parts.any { it == null }) null else parts.joinToString(", ") { displayValue(it) }
}

/**
 * The header an array element shows above its sub-form: its `[i]` index, followed by the branch title joined to
 * its primary-key value(s) when it is keyed -- "[1] Yearly — 2024" for a keyed trait, "[1] 2024" when the
 * element is not a union, and just "[1]" when it carries no key at all (issue #487).
 *
 * The index stays, always, because a validation failure is reported by path (`edits[1].data.year`) and the
 * listing lets a reader jump to `[1]`; dropping it would leave that message naming a row nothing on screen
 * carried. The key is added beside it, not in place of it, so several entries of one trait are still told apart
 * at a glance. Pure, and covered under `jsNodeTest`.
 */
fun elementRowHeader(
    elementType: SchType,
    element: Map<String, Any?>,
    index: Int,
    friendly: Boolean,
    layouts: Map<String, SchLayout> = emptyMap(),
): String {
    val key = keyedElementLabel(elementType, element)
    val title = elementType.variants?.let { v ->
        (element[v.discriminator] as? String)?.let { if (friendly) friendlyBranchLabel(v, it, layouts) else it }
    }
    val label = when {
        key != null && title != null -> "$title — $key"
        key != null -> key
        else -> null
    }
    return if (label != null) "[$index] $label" else "[$index]"
}

/**
 * How a friendly form names a union's branch [value]: the branch of an entries union -- discriminated on `traitId` --
 * by its trait's name (`branchDisplayName`, issue #1520), which [layouts] supplies the heading for; any other branch by
 * its title, else its value humanized. Pure, and covered under `jsNodeTest`.
 */
fun friendlyBranchLabel(variants: SchVariants, value: String, layouts: Map<String, SchLayout>): String =
    if (variants.discriminator == GE.traitId) {
        branchDisplayName(value, variants, layouts)
    } else {
        variants.byValue[value]?.title ?: humanizeFieldName(value)
    }

external interface SchemaFormProps : Props {
    /** The object type whose properties to render. */
    var type: SchType
    /** Current values by field name (a nested Kotlin Map/List tree). */
    var values: Map<String, Any?>
    /** When true, widgets are editable and call [onChange]; when false, they render disabled. */
    var editable: Boolean
    /**
     * Friendly data-entry presentation (issue #408): fields labeled by title/humanized key, derived system
     * fields hidden. Optional and read as false when absent -- the catalog does not set it and is unchanged.
     */
    var friendly: Boolean?
    /** Root-level field names to omit from the form (see [FormOpts.omit]); absent means omit nothing. */
    var omit: List<String>?
    /**
     * The caller's delivered cfacts (issue #564), `name -> present`, from the catalog response
     * ([Catalog.cfacts]). A property's `g-visibleWhen` is evaluated against these and the field hidden when it
     * fails -- in an *editable* form only (a read-only render documents the wire and shows every field). Absent
     * means no gating (every field shows).
     */
    var cfacts: Map<String, Boolean>?
    /**
     * The per-type field layouts (issue #586), keyed by qualified type name, from the surface's delivery
     * ([Catalog.fieldLayouts] / [WorkflowView.fieldLayouts]). In **friendly** mode a field's `label` / `description`
     * come from the layout for its enclosing type when it addresses the field, cascading over the schema's
     * `title` / `description`; absent (or in wire-documenting mode) the form is unchanged. A `${'$'}{…}` in the
     * copy is resolved against the object's own values through [evalTemplate].
     */
    var fieldLayouts: Map<String, SchLayout>?
    /**
     * Validation failures to show against the fields that caused them. Their paths are the ones the kernel
     * validator reported, and the form rebuilds the same paths as it walks — see [FieldErrors].
     *
     * Optional, and genuinely so: a form rendering a *response* has nothing to report against. Declaring it
     * non-null would not make it safe either -- an external interface is erased on the JS side, so an omitted
     * prop arrives as `undefined` no matter what the Kotlin type says, and the first call on it throws.
     */
    var failures: List<SchFailure>?
    /** Called with the full new values map for this object whenever a field changes. */
    var onChange: (Map<String, Any?>) -> Unit
    /**
     * Called with the path of the field the user just edited, so its now-stale failures can be dropped. Fired
     * from the innermost thing touched, never from the parent updates that bubble up behind it — editing one
     * field must not clear a sibling's error. Optional for the same reason as [failures]: a read-only form
     * has no edits to report.
     */
    var onFieldEdit: ((String) -> Unit)?
    /**
     * Called with the path of a field the user has just **finished with** (issue #718): a text box on blur, a
     * choice, date, checkbox or file on the selection itself, which is where those commit. The counterpart of
     * [onFieldEdit] -- that one says a value is changing, this one says it has settled -- so a form can check
     * input at commit points rather than per keystroke (which would flag "required" on a field the moment it is
     * tabbed into) or only on Save. Optional: a form that validates on Save alone leaves it unset.
     */
    var onFieldCommit: ((String) -> Unit)?
    /**
     * Promote a keyed object property's primary-key fields out of the nested object and up to its own level
     * (issue #642) -- for the edit form, so a trait's key shows beside the trait choice rather than buried in
     * `data`, and a delete is key-only with an empty data section. Purely presentational: the value stays in
     * wire shape (the key is read and written inside the nested object), so the submitted payload is unchanged.
     * Optional, off by default -- only the edit form sets it, so every other form is unaffected.
     */
    var promoteKeys: Boolean?
    /**
     * How each root field presents a supplied default (issue #710), keyed by field name; see [FormOpts.prefill].
     * Optional and off by default -- only the workflow form, rendering a view that carries prefills, sets it.
     */
    var prefill: Map<String, FieldPrefill>?
    /**
     * Let a trait union's `traitId` be typed freely, not only chosen (issue #667); see [FormOpts.openTraitEntry].
     * Optional, off by default -- only the edit form, on the cross-client admin (shared/global) surface, sets it.
     */
    var openTraitEntry: Boolean?
    /**
     * Show a derived *content* field read-only when it holds a value (issue #712); see
     * [FormOpts.showDerivedValues]. Optional, off by default -- only the read-only form surfaces set it, paired
     * with a boundary ([derivedRootIsTraitData] or [traitDataField]) that says where trait content lives, and it
     * acts only when the form is not editable.
     */
    var showDerivedValues: Boolean?
    /** The whole form root is a trait's data payload (issue #712); see [FormOpts.derivedRootIsTraitData]. */
    var derivedRootIsTraitData: Boolean?
    /** The property name marking a trait's data payload within a document (issue #712); see [FormOpts.traitDataField]. */
    var traitDataField: String?
    /** A note to draw beside a list element's header (issue #857); see [FormOpts.elementNote]. */
    var elementNote: ((path: String, element: Map<String, Any?>) -> String?)?
}

/**
 * The per-field validation state a render pass carries: which messages to show under a field, and how to
 * report that a field was edited.
 *
 * It exists, so the two travel together through the traversal rather than as two more parameters on every
 * render function, and so the grouping is done once for the pass instead of rescanning the failure list per
 * field. [noteCommit] rides along for the same reason (issue #718): a field's commit is reported from the
 * widget that owns the control, which is as deep as the traversal goes.
 */
class FieldErrors(
    private val all: List<SchFailure>,
    val noteEdit: (String) -> Unit,
    val noteCommit: (String) -> Unit = {},
) {
    private val byPath = all.byPath()

    /** The failures reported at exactly [path]; empty when the field is fine (or when nothing was validated). */
    fun messagesAt(path: String): List<SchFailure> = byPath[path] ?: emptyList()

    /**
     * The failures to show under a field drawn as **one control over a whole object** -- the JSON editor an object
     * with no declared fields gets, a map's among them (issue #1055): those at [path], then those below it, each led
     * by where in the object it is. The control has no slot per key, so without this a failure inside a map's entry
     * would be marked nowhere and named only in the listing at the foot of the page.
     */
    fun messagesWithin(path: String): List<SchFailure> = messagesAt(path) +
        all.filter { it.path != path && isPathAtOrBelow(it.path, path) }
            .map { it.copy(message = "${it.path.substring(path.length).trimStart('.')}: ${it.message}") }

    /**
     * Failures below [path] whose next key down is not one of [declared] — reported against a property this
     * object does not have, so no field of its own will ever be drawn for it. Grouped by their own path, so
     * each gets one addressable place to appear.
     */
    fun undeclaredBelow(path: String, declared: Set<String>): Map<String, List<SchFailure>> =
        all.filter { f -> childKeyOf(f.path, path)?.let { it !in declared } == true }.byPath()

    /**
     * Any failure reported strictly *below* [path] -- an element or a cell of an array, not the array field
     * itself (which the field frame already shows). A table has no per-cell slot for these, so their presence
     * is what sends an array to the block form instead (issue #540).
     */
    fun anyUnder(path: String): Boolean = all.any { it.path != path && isPathAtOrBelow(it.path, path) }
}

/**
 * The DOM id of a field's row, and of the block holding its messages. Derived from the path so anything
 * holding a failure can find its field: the listing at the foot of the page, and Validate focusing the first
 * problem.
 *
 * Looked up with `getElementById`, never a CSS selector — a path contains `.` and `[`, which are selector
 * syntax and would need escaping, while `getElementById` takes the string literally. Paths carry no spaces,
 * so they are also valid as the `aria-describedby` IDREF that ties a control to its message.
 */
fun fieldRowId(path: String): String = "fld-$path"

fun fieldErrorsId(path: String): String = "err-$path"

/** The id of the note naming the zone a date-time field at [path] is read in (issue #1235), for `aria-describedby`. */
fun fieldZoneNoteId(path: String): String = "zone-$path"

/**
 * Brings the field at [path] into view and puts focus on it, or does nothing when no field is rendered there.
 *
 * Focus lands on the first control inside the row when there is one, and on the row itself otherwise — a
 * list header or an object header has no control of its own, and giving every row `tabIndex = -1` makes it
 * focusable programmatically without adding a tab stop for someone moving through the form by keyboard.
 */
fun focusField(path: String) {
    // The plain DOM `document` here, whose getElementById takes the id as a String; the `id` prop on the
    // React side is the wrappers' ElementId. Same string either way.
    val row = document.getElementById(fieldRowId(path)) ?: return
    row.asDynamic().scrollIntoView(js("({ block: 'center', behavior: 'smooth' })"))
    val control = row.querySelector("input, select, textarea, button") as? HTMLElement
    (control ?: row as? HTMLElement)?.focus()
}

/**
 * Sends focus to the first failing field once the render carrying [failures] has committed (issues #408, #417).
 * An effect, not a direct call: the field is addressed by a DOM id that does not exist until React has drawn the
 * failures a check produced. Keyed on [focusRequest] -- a counter a check bumps -- rather than on [failures], so
 * clearing one failure by editing its field does not steal focus out of the field being fixed. Shared by the
 * create and edit form pages, which drive the identical behaviour.
 */
fun useFocusOnFailure(focusRequest: Int, failures: List<SchFailure>?) {
    useEffect(focusRequest) {
        if (focusRequest > 0) {
            failures?.firstOrNull()?.let { focusField(it.path) }
        }
    }
}

/**
 * A `g-visibleWhen` evaluator over the caller's delivered cfacts (issue #564): parses an expression with the
 * SAME kernel [CFactParser] the backend uses and matches it against the present ones. [cfacts] is the map the
 * catalog delivered -- every frontend cfact name to whether it is present -- so its keys are the parse
 * vocabulary and its true-valued keys are the present set. Null means no cfacts were supplied, so nothing is
 * gated (every field shows); a form opts into gating by passing them.
 *
 * Fail-open on a parse error: the boot check already validated every expression against the registry, so a
 * throw here means the delivered vocabulary lacks a name the gate uses -- show the field rather than hide it on
 * a technicality, since this is presentation and the backend enforces the real condition.
 */
internal fun buildCfactGate(cfacts: Map<String, Boolean>?): (String) -> Boolean {
    if (cfacts == null) return { true }
    val allowed = cfacts.keys
    val present = cfacts.filterValues { it }.keys
    val cache = HashMap<String, Boolean>()
    return { expression ->
        cache.getOrPut(expression) { CFactParser.analyze(expression, allowed, evaluateWith = present).value ?: true }
    }
}

/**
 * Renders a kernel [SchType] as a form — the generic display engine. It dispatches each field to a widget by
 * the field's parsed schema (`jsonType` / `format` / `options` / `itemType` / nested `properties`), marking
 * required fields from the parent type's `required` set. A nested object recurses into an indented sub-form;
 * a self-referential type ([SchType.name] already seen on the path) renders a collapsed marker instead of
 * expanding forever.
 *
 * One dispatch serves both faces: with [SchemaFormProps.editable] false, widgets render disabled (the read-only
 * view); with it true, they call back through [SchemaFormProps.onChange], which threads an immutable value
 * update up to the top. The kernel validator ([EndpointCatalog]) checks the assembled values with the exact
 * backend logic.
 */
val SchemaForm = FC<SchemaFormProps> { props ->
    val errors = FieldErrors(props.failures ?: emptyList(), props.onFieldEdit ?: {}, props.onFieldCommit ?: {})
    // An external-interface Boolean arrives as `undefined` when a caller omits it, so read it as `== true`
    // rather than trusting the declared type; the catalog omits both and gets the plain wire view.
    val opts = FormOpts(
        friendly = props.friendly == true,
        omit = props.omit?.toSet() ?: emptySet(),
        gateAllows = buildCfactGate(props.cfacts),
        fieldLayouts = props.fieldLayouts ?: emptyMap(),
        promoteKeys = props.promoteKeys == true,
        prefill = props.prefill ?: emptyMap(),
        openTraitEntry = props.openTraitEntry == true,
        showDerivedValues = props.showDerivedValues == true,
        derivedRootIsTraitData = props.derivedRootIsTraitData == true,
        traitDataField = props.traitDataField,
        elementNote = props.elementNote ?: { _, _ -> null },
        design = use(DesignViewContext),
    )
    div {
        // `friendly` on the root lets the stylesheet give a data-entry / read form's field groups room to breathe
        // while the wire-documenting catalog keeps its density (issue #694 review); see `.schema-form.friendly`.
        className = ClassName(if (opts.friendly) "schema-form friendly" else "schema-form")
        // The root path is empty, which is what the validator starts from too, so `childPath` composes the
        // identical strings on both sides (see SchValidator's note on why these are shared).
        renderObject(props.type, props.values, emptySet(), props.editable, "", errors, props.onChange, opts)
    }
}

/** Renders an object type's fields, threading the cycle-guard [seen] set (visited `$ref` type names). */
private fun ChildrenBuilder.renderObject(
    type: SchType,
    values: Map<String, Any?>,
    seen: Set<String>,
    editable: Boolean,
    path: String,
    errors: FieldErrors,
    onChange: (Map<String, Any?>) -> Unit,
    opts: FormOpts,
    // Fields to leave out of this object's render (issue #642): the primary-key fields a parent promoted, drawn
    // there instead. Applies to this object only; children start fresh.
    hideFields: Set<String> = emptySet(),
) {
    type.variants?.let { variants ->
        renderVariant(type, variants, values, seen, editable, path, errors, onChange, opts)
        return
    }
    if (type.properties.isEmpty()) {
        p {
            className = ClassName("type-hint")
            +"(no parameters)"
        }
        return
    }
    renderProperties(type, values, seen, editable, path, errors, onChange, opts, hideFields = hideFields)
    // Then anything reported against a key this object does not declare. Nothing above draws these -- there is
    // no property to render -- so without this they exist only in the listing at the foot of the page, which
    // is precisely the "named somewhere you cannot go" problem this issue is about.
    errors.undeclaredBelow(path, type.properties.keys).forEach { (at, messages) ->
        undeclaredField(childKeyOf(at, path) ?: at, at, messages)
    }
}

/**
 * The direct object properties of [type] whose value type declares a primary key (issue #642), each mapped to
 * its key fields -- the fields to hoist to [type]'s own level and hide inside the object. Only a single keyed
 * **object** promotes: a keyed array is left alone, since "alongside the choice" has no meaning for a list, and a union
 * cannot be keyed (the parse refuses a key on a type declaring no fields for it, issue #1521). This is the
 * shape a trait's `data` takes on an edit branch, but the rule names no trait -- it knows only that an object
 * property is keyed, the way the keyed-element labelling does. Pure, so a jsNodeTest can pin which fields lift.
 */
fun promotableKeys(type: SchType): Map<String, List<String>> =
    type.properties
        .filterValues {
            it.valueType.jsonType == SCT.kObject && it.valueType.primaryKey.isNotEmpty()
        }
        .mapValues { it.value.valueType.primaryKey }

/**
 * Renders a type's declared properties, applying its conditional-presence rule if it has one (issue #253).
 *
 * Shared by an ordinary object and by a union's selected branch, which is not tidiness but correctness: a
 * branch is an ordinary type that happens to have been chosen, so a rule declared on it has to apply exactly
 * as it would anywhere else. When [renderVariant] had a property loop of its own, a conditional inside a
 * branch was silently ignored — the schema said it, the validator enforced it, and the form knew nothing
 * about it, which is the worst of the three places to disagree.
 *
 * [skip] is how the union keeps its discriminator out: that one is drawn by the union itself, as a choice
 * rather than as the fixed value the branch declares.
 */
private fun ChildrenBuilder.renderProperties(
    type: SchType,
    values: Map<String, Any?>,
    seen: Set<String>,
    editable: Boolean,
    path: String,
    errors: FieldErrors,
    onChange: (Map<String, Any?>) -> Unit,
    opts: FormOpts,
    skip: String? = null,
    // Fields to leave out of this object's loop (issue #642): a primary-key field a parent hoisted out, drawn
    // there instead of here. The discriminator [skip] is the sibling case -- drawn by the union above.
    hideFields: Set<String> = emptySet(),
) {
    // A conditional-presence rule decides, from what the watched field currently holds, which properties are
    // required and which may not appear at all.
    val condition = type.condition
    val holds = condition?.holds(values) ?: false
    val forbidden = condition?.forbiddenWhen(holds) ?: emptySet()
    val alsoRequired = condition?.requiredWhen(holds) ?: emptySet()
    // Changing the watched field re-decides the rule, so anything it now forbids has to go with it. The same
    // reasoning as switching a union's branch: leaving the value behind puts a field on the wire that
    // validation refuses, and the form is no longer showing, which is unfixable from the screen.
    fun settle(next: Map<String, Any?>, edited: String): Map<String, Any?> =
        if (condition == null || edited != condition.property) next
        else next - condition.forbiddenWhen(condition.holds(next))

    // Primary-key promotion (issue #642): when opted in, a keyed object property's key fields are drawn here,
    // at this object's own level -- right under a union's trait choice, drawn just above -- and hidden inside
    // the object below (its key names go down as that field's `hideFields`). Each promoted widget reads and
    // writes the key *inside* the nested object, so the value never leaves wire shape and nothing is pushed
    // back down on submit. Empty unless the caller opted in and a property is a keyed object.
    val promoted = if (opts.promoteKeys) promotableKeys(type) else emptyMap()
    for ((objName, keyFields) in promoted) {
        val objType = type.properties[objName]?.valueType ?: continue
        val subMap = (values[objName] as? Map<*, *>)?.toJsonMapOrEmpty() ?: emptyMap()
        // A key is drawn from the nested object, so the nested object's own rules decide it -- the same guards
        // the property loop below applies, evaluated here against that object and its values (issue #642), so
        // the promotion cannot turn a derived or gated key into an editable box the schema says nobody supplies.
        val objCond = objType.condition
        val objForbidden = objCond?.forbiddenWhen(objCond.holds(subMap)) ?: emptySet()
        val objAlsoRequired = objCond?.requiredWhen(objCond.holds(subMap)) ?: emptySet()
        for (keyName in keyFields) {
            val keyProp = objType.properties[keyName] ?: continue
            val derived = keyProp.valueType.derived
            if (opts.friendly && derived) continue
            if (editable && keyProp.visibleWhen?.let { !opts.gateAllows(it) } == true) continue
            if (keyName in objForbidden && isBlankValue(subMap[keyName])) continue
            renderField(
                keyName, keyProp,
                (keyName in objType.required || keyName in objAlsoRequired) && !derived,
                subMap[keyName], seen, editable && !derived,
                childPath(childPath(path, objName), keyName), errors,
                emit = { newVal ->
                    val cur = (values[objName] as? Map<*, *>)?.toJsonMapOrEmpty() ?: emptyMap()
                    onChange(values + (objName to (cur + (keyName to newVal))))
                },
                omit = {
                    val cur = (values[objName] as? Map<*, *>)?.toJsonMapOrEmpty() ?: emptyMap()
                    onChange(values + (objName to (cur - keyName)))
                },
                opts = opts,
                copy = layoutCopy(objType, keyName, subMap, opts),
            )
        }
    }

    // Which fields to draw, in what order, and why any are left out (issue #972): one decision, made by
    // [formFieldPlan], which both presentations follow -- the ordinary form skips a field it hides, and Design View
    // draws the same field as a ghost saying why. Two views computed apart would drift, and Design View would then
    // misrepresent the very form it exists to explain.
    formFieldPlan(type, values, path, editable, forbidden, opts, skip, hideFields).forEach { planned ->
        val name = planned.name
        val prop = planned.prop
        planned.hidden?.let { why ->
            if (opts.design?.showHidden == true) {
                designMarked(type, path, name, prop, why, opts) {
                    renderField(
                        name, prop, false, values[name], seen, false, childPath(path, name), errors,
                        emit = {}, omit = {}, opts = opts, copy = layoutCopy(type, name, values, opts),
                    )
                }
            }
            return@forEach
        }
        // A derived value is produced by something other than whoever is filling this in (issue #254), so it
        // is shown and never offered: no box to type in, in either mode.
        //
        // Shown rather than hidden, because this surface documents the wire -- the same reason it labels
        // fields with their key rather than a `title`. Hiding a field the contract contains is the
        // data-entry instinct applied to a tool for reading a contract, and it cost real confusion: with the
        // computed field invisible, a value appearing in the response reads as the one you typed being
        // overwritten. A real form-entry GUI would hide it, and would be right to; these two surfaces read
        // the same keyword and reach opposite conclusions, which is the point of the annotation.
        // The layout's copy override for this field (issue #586), resolved against the object's own values;
        // null elements fall through to the schema's title/description at the render site.
        val copy = layoutCopy(type, name, values, opts)
        designMarked(type, path, name, prop, null, opts) {
            renderField(
                // Never marked required, derived: the asterisk means "you must supply this", and a field with no
                // control is not something anybody can supply. It is required of the *stored* shape, which the
                // response demonstrates by carrying it.
                name, prop,
                // Or required on this form by its layout (issue #1022): drawn here, so the schema offers it now.
                (name in type.required || name in alsoRequired || copy?.entry?.required == true) && !prop.valueType.derived,
                values[name], seen,
                editable && !prop.valueType.derived,
                childPath(path, name), errors,
                // A removal has to drop the key, not null it: a null against an object/array type fails the plain
                // type check (they do not coerce), so "removed" would read as "present but wrong".
                emit = { newValue -> onChange(settle(values + (name to newValue), name)) },
                omit = { onChange(settle(values - name, name)) },
                opts = opts,
                copy = copy,
                // A keyed object property whose keys were promoted above hides them within itself (issue #642).
                hideFields = promoted[name]?.toSet() ?: emptySet(),
                // A supplied default is presented at the top level only (issue #710) -- the presenting decision is
                // the trait's, and a same-named field one level down is a different field, like `omit` above.
                prefill = if (path.isEmpty()) opts.prefill[name] else null,
            )
        }
    }
}

/** One field of an object's form, in drawing order, and -- when the form leaves it out -- why (issue #972). */
class PlannedField(val name: String, val prop: SchProperty, val hidden: FieldHidden?)

/**
 * The fields of [type] an object's form considers, in order, each with the reason it is left out or null when it is
 * drawn (issue #972). The one decision both presentations follow: the ordinary form draws the fields with no reason,
 * and Design View additionally draws the rest as ghosts saying why -- so "the ordinary form is Design View minus its
 * ghosts" holds by construction, and a `jsNodeTest` pins it.
 *
 * The order and candidate set come from the kernel seam (issue #777): schema order under an overlay layout (the
 * default), the layout's order and membership under `reorder`/`authoritative`. A field an authoritative layout
 * leaves out follows at the end as [FieldHidden.notInLayout]. [skip] (a union's discriminator) and [hideFields] (a
 * promoted primary key) are not hidden but drawn elsewhere, so they are not planned here at all.
 */
fun formFieldPlan(
    type: SchType,
    values: Map<String, Any?>,
    path: String,
    editable: Boolean,
    forbidden: Set<String>,
    opts: FormOpts,
    skip: String? = null,
    hideFields: Set<String> = emptySet(),
): List<PlannedField> {
    val ordered = orderedFieldNames(type, type.name?.let { opts.fieldLayouts[it] })
    val drawnElsewhere = hideFields + listOfNotNull(skip)
    val considered = ordered.filter { it !in drawnElsewhere }.mapNotNull { name ->
        type.properties[name]?.let { PlannedField(name, it, fieldHiddenReason(name, it, path, values, editable, forbidden, opts)) }
    }
    val leftOut = type.properties.filterKeys { it !in ordered && it !in drawnElsewhere }
        .map { (name, prop) -> PlannedField(name, prop, FieldHidden.notInLayout) }
    return considered + leftOut
}

/**
 * Why an object's form leaves field [name] out, or null when it draws it (issue #972); see [formFieldPlan]. The
 * per-field gates, which run on top of the layout's order and membership, so a layout narrows and orders but never
 * widens past what the schema shows.
 */
fun fieldHiddenReason(
    name: String,
    prop: SchProperty,
    path: String,
    values: Map<String, Any?>,
    editable: Boolean,
    forbidden: Set<String>,
    opts: FormOpts,
): FieldHidden? = when {
    // A field the form was told to omit, only at the root: an advanced flag an end-user form should not offer (issue
    // #408). A same-named field deeper in the tree is a different field and stays.
    path.isEmpty() && name in opts.omit -> FieldHidden.omitted
    // In friendly mode a derived value is not shown at all -- it is produced by something other than the person at
    // the form (an id, the source, the audit stamps), so it has no place on a data-entry screen. The catalog does the
    // opposite and shows it read-only, because that surface documents the wire.
    //
    // The exception (issue #712): a read-only friendly form that opts in shows a derived *content* value that holds
    // one -- an expense report's total, computed by the backend -- read-only, while a derived *envelope* field (an id, an
    // entry's audit stamps) stays hidden. `revealsDerivedAt` draws that content/envelope line structurally, from the
    // field's path. The render path already draws a shown derived field read-only and never marks it required.
    opts.friendly && prop.valueType.derived && !opts.revealsDerivedAt(childPath(path, name), editable, values[name]) ->
        FieldHidden.derived
    // A field gated by g-visibleWhen (issue #564) is hidden when the caller's cfacts fail its expression -- but only in
    // an *editable* form, where the caller would be entering a value. A read-only render (the catalog documenting the
    // wire, a stored form on display) shows the field regardless, and so does a form that supplies no cfacts. This is
    // presentation, not access control: for trait data the backend neither refuses a gated value on write nor
    // withholds it on read, so hiding the box is the whole of the gate. (An endpoint input may be enforced by its own
    // handler -- the admin-only list filters are.)
    editable && prop.visibleWhen?.let { !opts.gateAllows(it) } == true -> FieldHidden.notForViewer
    // A forbidden field is hidden -- unless it still holds something, in which case hiding it would hide its failure
    // too, leaving a complaint about a field with nowhere to go and no way to clear it. Shown, it carries its own "not
    // allowed" message and can be emptied.
    name in forbidden && isBlankValue(values[name]) -> FieldHidden.forbidden
    else -> null
}

/**
 * Draws [content] -- one field of [type] at [path] -- marked for Design View (issue #972): outlined, carrying its
 * name as a badge, and selectable into the inspector; a [hidden] field is drawn as a ghost with its reason. Without
 * a Design View session (or outside a trait, which a workflow form always supplies) it draws [content] alone, so an
 * ordinary form is untouched.
 */
private fun ChildrenBuilder.designMarked(
    type: SchType,
    path: String,
    name: String,
    prop: SchProperty,
    hidden: FieldHidden?,
    opts: FormOpts,
    content: ChildrenBuilder.() -> Unit,
) {
    val session = opts.design
    val trait = session?.trait
    if (session == null || trait == null) {
        content()
        return
    }
    val target = DesignTarget.Field(
        trait, childPath(path, name), name, prop, name in type.required,
        type.name?.let { opts.fieldLayouts[it] }?.fieldFor(name), hidden,
    )
    designTargetFrame(session, target, name, ghostReason = hidden?.reason, altered = session.isAltered(target), content = content)
}

/**
 * Whether a field currently holds nothing worth showing — absent, or text someone has cleared.
 *
 * Matches the kernel's own `emptyIsAbsent` reading of a scalar closely enough for the one decision it makes
 * here (whether a now-forbidden field still needs to be on screen). It deliberately does not try to be that
 * rule: the authority on whether a value counts is the validator, and this only chooses what to draw.
 */
private fun isBlankValue(value: Any?): Boolean = value == null || (value is String && value.isBlank())

/**
 * Renders a discriminated union: a choice of branch, then that branch's own fields (issue #252).
 *
 * The discriminator is drawn **here** rather than by the branch, even though every branch declares it. A
 * branch declares it as a `const` — one fixed value, which is a statement about the shape rather than a
 * choice — so left to the branch it would render as a text box that cannot change the shape it belongs to.
 * Drawn here it is a select over the declared values, which is the one control that makes a union editable.
 *
 * **Switching branches drops what the new branch would not accept.** Branches are closed objects, so a field
 * the new branch does not declare, or declares with a *different shape*, would turn the switch into a payload
 * full of undeclared-property failures against fields the form is no longer even showing — invisible and
 * unfixable. A field the new branch declares with the **same** type is kept, since re-typing a shared field to
 * change one next to it is the kind of thing that makes a form tiring — but a same-*named* field of a different
 * shape is not, or the old branch's sub-fields ride along inside it (the edit union's per-trait `data` is
 * exactly this, issue #417). [valuesAfterBranchSwitch] is the rule.
 */
private fun ChildrenBuilder.renderVariant(
    type: SchType,
    variants: SchVariants,
    values: Map<String, Any?>,
    seen: Set<String>,
    editable: Boolean,
    path: String,
    errors: FieldErrors,
    onChange: (Map<String, Any?>) -> Unit,
    opts: FormOpts,
) {
    val name = variants.discriminator
    val chosen = values[name].toOptStr()
    val branch = variants.select(chosen)
    val discriminatorPath = childPath(path, name)
    val messages = errors.messagesAt(discriminatorPath)
    // Captured under its own name: inside the Select builder `onChange` is the widget's own prop, and `values`
    // and `options` are likewise taken.
    val emitAll = onChange
    // In friendly mode each choice reads by its name ([friendlyBranchLabel]); the value sent stays the wire
    // discriminator, so only the label changes. The catalog shows the value itself, documenting the wire.
    val schChoices = variants.values.map { value ->
        val label = if (opts.friendly) friendlyBranchLabel(variants, value, opts.fieldLayouts) else value
        SchOption(value, label)
    }
    val choices = optionsToJs(schChoices)
    val describedBy = messages.ifEmpty { null }?.let { fieldErrorsId(discriminatorPath) }
    // Carry what the new branch would still accept, and drop the rest (see the note above and
    // [valuesAfterBranchSwitch]); `branch` is the branch selected *before* the switch. Re-picking the same value
    // changes nothing, so the values pass through untouched rather than through the switch logic, which would
    // treat a branch-specific field as one to drop. An unknown typed value (the open-entry case) selects the
    // union's default branch, so its free-form `data` shows -- see [SchVariants.select].
    val switchTo: (String?) -> Unit = { picked ->
        errors.noteEdit(discriminatorPath)
        if (picked == chosen) {
            emitAll(values)
        } else {
            emitAll(valuesAfterBranchSwitch(values, name, branch, variants.select(picked), picked, seedObjects = opts.friendly))
        }
    }
    // Free-form trait entry (issue #667): a trait union's `traitId` is typed, not only chosen, so a cross-client
    // admin can name a trait this node's global union does not list. Only the `traitId` discriminator opens.
    val openEntry = opts.openTraitEntry && name == GE.traitId

    div {
        id = ElementId(fieldRowId(discriminatorPath))
        tabIndex = -1
        className = ClassName(rowClass(messages))
        labelSpan(if (opts.friendly) humanizeFieldName(name) else name, required = true)
        if (editable && openEntry) {
            OpenChoiceField {
                // Labels are the raw trait ids, not the friendly branch titles the closed Select shows: antd's
                // combobox puts the option's VALUE in the box after a pick (webapp/CLAUDE.md), so a friendly
                // label would read one way in the list and the bare id in the box. On this free-form control the
                // id is what a person types for an unknown client's trait anyway, so list and box agree (#667).
                options = variants.values.map { SchOption(it, it) }
                value = chosen
                this.describedBy = describedBy
                onEmit = switchTo
                onCommit = { errors.noteCommit(discriminatorPath) }
            }
        } else if (editable) {
            Select {
                this.options = choices
                this.value = chosen
                placeholder = "(choose)"
                style = choiceStyle()
                this.onChange = { v -> switchTo(v as? String) }
                markInvalid(asDynamic(), describedBy)
            }
        } else {
            // Not `readOnlyValue(type, …)`: `type` here is the union, so it would label the discriminator
            // "(object)" -- the shape it selects rather than the value it holds.
            chosen?.let {
                span {
                    className = ClassName("field-value")
                    +if (opts.friendly) friendlyBranchLabel(variants, it, opts.fieldLayouts) else it
                }
            }
            span {
                className = ClassName("field-type")
                +"(choice)"
            }
        }
    }
    fieldErrors(discriminatorPath, messages)

    if (branch == null) {
        p {
            className = ClassName("type-hint")
            +"Choose a $name to see the rest of the fields."
        }
        return
    }
    // The branch's own fields, minus the discriminator it declares -- already drawn above, and as a choice
    // rather than as the fixed value the branch states. Through the shared loop, so a rule declared on the
    // branch (a conditional, say) applies exactly as it would on any other type.
    renderProperties(branch, values, seen, editable, path, errors, onChange, opts, skip = name)
    errors.undeclaredBelow(path, branch.properties.keys).forEach { (at, messages2) ->
        undeclaredField(childKeyOf(at, path) ?: at, at, messages2)
    }
}

/**
 * Whether a field's current value may be carried across a switch of a union's branch (issue #417) — kept when
 * [to] declares the field with a type that would still accept the value, dropped otherwise.
 *
 * A shared **scalar** field carries over on a matching JSON type: the edit union declares `action` and
 * `entryId` identically on every branch, so switching a trait keeps the verb beside it rather than blanking it.
 * A **structured** field (an object, a union, or an array) carries over only when both branches name the *same*
 * type. A branch-specific object like the edit union's `data` is a different type per trait, so keeping it would
 * leave the old trait's sub-fields on the wire against a new branch that forbids them — and, since the form now
 * draws the new branch, with no field on screen to clear them from. That is the "Additional property … is not
 * allowed" report this fixes. Pure, and covered under `jsNodeTest`.
 */
fun fieldCarriesAcrossBranches(from: SchType, to: SchType): Boolean =
    if (isStructuredObject(from) || isStructuredObject(to) || from.jsonType == SCT.array || to.jsonType == SCT.array) {
        // A structured shape carries only as the *same named type*: a differently-named or inline object is
        // treated as different, so its stale contents are dropped rather than left to fail the new branch.
        from.name != null && from.name == to.name
    } else {
        from.jsonType == to.jsonType
    }

/**
 * The values that remain after a discriminated union switches from the [from] branch to the [to] branch, with
 * [picked] the newly chosen discriminator value (issue #417).
 *
 * The discriminator ([discriminator]) is set to [picked]; every other field is kept only when both branches
 * declare it and [fieldCarriesAcrossBranches] says the new branch would still accept it. A [to] of null (the
 * choice was cleared, or names no branch) keeps nothing but the discriminator. Pure, and covered under
 * `jsNodeTest`.
 *
 * **When [from] is null there is no old branch to compare against** — nothing was selected before, so there is
 * no stale branch shape that could contaminate the payload — and the field-by-field type check would drop
 * *everything*. There it falls back to the plain rule (keep whatever the new branch declares), which is what
 * the switch did before per-field type comparison existed: applying values with a missing or unknown
 * discriminator and then choosing a branch must not silently discard the fields that branch accepts.
 */
fun valuesAfterBranchSwitch(
    values: Map<String, Any?>,
    discriminator: String,
    from: SchType?,
    to: SchType?,
    picked: String?,
    // Seed the new branch's plain-object properties to an empty map (issue #662), so a data-entry form shows
    // their fields to fill rather than an "Add" control -- the edit union's `data` in particular, dropped by the
    // switch. Off by default: only a friendly form asks for it; the wire-documenting catalog leaves them absent.
    seedObjects: Boolean = false,
): Map<String, Any?> {
    val kept = when {
        to == null -> emptyMap()
        // No old branch: keep what the new branch declares (see the note above) -- there is no old shape to
        // decide a same-named field is incompatible, so the type comparison has nothing to run against.
        from == null -> values.filterKeys { it != discriminator && it in to.properties }
        else -> values.filterKeys { key ->
            if (key == discriminator) return@filterKeys false
            val toProp = to.properties[key] ?: return@filterKeys false
            val fromProp = from.properties[key] ?: return@filterKeys false
            fieldCarriesAcrossBranches(fromProp.valueType, toProp.valueType)
        }
    }
    var out = kept + (discriminator to picked)
    if (seedObjects && to != null) {
        for ((name, prop) in to.properties) {
            val vt = prop.valueType
            val plainObject = vt.variants == null && vt.jsonType == SCT.kObject && vt.properties.isNotEmpty()
            if (plainObject && out[name] == null) out = out + (name to emptyMap<String, Any?>())
        }
    }
    return out
}

/**
 * A failure against a key the schema does not declare. It borrows the shape of a field row so it reads in
 * place and carries the same id, so the listing can jump to it like any other — but it shows the **key**
 * rather than a label, since there is no declared property behind it to have a description or a type.
 */
private fun ChildrenBuilder.undeclaredField(key: String, path: String, messages: List<SchFailure>) {
    div {
        id = ElementId(fieldRowId(path))
        tabIndex = -1
        className = ClassName("row field-invalid")
        span {
            className = ClassName("field-label")
            +key
        }
        span {
            className = ClassName("field-type")
            +"(not in the schema)"
        }
    }
    fieldErrors(path, messages)
}

/**
 * Renders one field, dispatching on its shape: a list of objects, a nested object, or a labeled widget row.
 * [emit] reports this field's new value up to its parent object; [omit] drops the field entirely.
 */
private fun ChildrenBuilder.renderField(
    name: String,
    prop: SchProperty,
    required: Boolean,
    value: Any?,
    seen: Set<String>,
    editable: Boolean,
    path: String,
    errors: FieldErrors,
    emit: (Any?) -> Unit,
    omit: () -> Unit,
    opts: FormOpts,
    copy: LayoutCopy? = null,
    // Fields to hide inside this field when it is a nested object (issue #642): its promoted primary-key fields.
    hideFields: Set<String> = emptySet(),
    // How this field presents a supplied default (issue #710); null for every field but a prefilled root scalar.
    prefill: FieldPrefill? = null,
) {
    val vt = prop.valueType
    val elementType = objectElementType(vt)
    if (elementType != null) {
        // A read-only array whose element type declares `presentation: table` (issue #540) renders as a real
        // table -- one row per element, its properties the columns -- rather than a stack of nested field
        // blocks. Editing still uses the block form (a table is not an editing affordance here). A table has
        // no per-cell error slot, so it is used only when it can tell the whole story: the element type has
        // columns to draw, and nothing failed *under* this array that the table would swallow. A union element
        // type (empty `properties`) or an element/cell failure falls through to the block form, which draws
        // each element and carries its failures.
        if (!editable && elementType.presentation == PRES.table &&
            elementType.properties.isNotEmpty() && !errors.anyUnder(path)
        ) {
            renderTable(name, prop, required, value, elementType, path, errors, opts, copy)
            return
        }
        renderObjectList(name, prop, required, value, elementType, seen, editable, path, errors, emit, omit, opts, copy)
        return
    }
    // A list of scalars, edited: one widget per element. Not the multi-select case (an item type with options
    // is a fixed set of choices, which the Select already emits as a real list), and not the read-only view,
    // where a comma-joined line reads better than a column of single values.
    if (editable && vt.jsonType == SCT.array && vt.itemType?.options == null) {
        renderScalarList(name, prop, required, value, vt.itemType, path, errors, emit, omit, opts, copy)
        return
    }
    if (isStructuredObject(vt)) {
        renderNestedObject(name, prop, required, value, vt, seen, editable, path, errors, emit, omit, opts, copy, hideFields)
        return
    }

    // An object reaching here has no declared fields and is one control: a failure below it -- inside a map's
    // entry -- is shown with the field, where the person editing it is.
    val messages = if (vt.jsonType == SCT.kObject) errors.messagesWithin(path) else errors.messagesAt(path)
    fieldFrame(name, prop, required, path, messages, opts, copy, value = value, prefill = prefill) {
        widget(
            vt, value, required, editable, messages.ifEmpty { null }?.let { fieldErrorsId(path) },
            // A hint declared at *this* use site wins over one on the (shared) target type -- the same
            // per-site precedence `title` takes (issue #540).
            presentation = prop.presentation ?: vt.presentation, opts = opts,
            // The form's own choices (issue #1022), when its layout restates them.
            choices = copy?.entry?.choices?.let { formChoiceList(vt, copy.entry, value) },
            zoneNoteId = fieldZoneNoteId(path),
            multiline = copy?.entry?.multiline == true,
            commit = { errors.noteCommit(path) },
        ) { newValue ->
            errors.noteEdit(path)
            emit(newValue)
        }
        // The supplied-default affordances (issue #710): an `offer` field's "Use it" link, or a touched
        // `filled` field's "reset to suggested". Applying an offer runs through the field's own edit path, so
        // it notes the edit like a keystroke; a reset restores the value and the suggestion mark on its own.
        prefillControls(prefill, value, editable) { v -> errors.noteEdit(path); emit(v) }
    }
}

/**
 * How every field presents *itself*, whatever it then goes on to contain: a row carrying the label and
 * whatever [rowContent] puts beside it, the field's description, and any failures reported against it.
 *
 * The four render paths differ in what they draw *after* this — a widget inline, an expanded sub-form, a
 * column of elements — but they must not differ in this, or a field's error appears in a different place
 * depending on the shape of its type. Having one frame is also what keeps the next addition to it (an
 * ancestor marker, an `aria-describedby` tying control to message) a single edit rather than four.
 */
private fun ChildrenBuilder.fieldFrame(
    name: String,
    prop: SchProperty,
    required: Boolean,
    path: String,
    messages: List<SchFailure>,
    opts: FormOpts,
    copy: LayoutCopy? = null,
    // The field's current value, so a layout error override can echo the offending value (issue #588).
    value: Any? = null,
    // How this field presents a supplied default (issue #710); a `filled` suggestion marks its label.
    prefill: FieldPrefill? = null,
    rowContent: ChildrenBuilder.() -> Unit = {},
) {
    div {
        id = ElementId(fieldRowId(path))
        // Focusable on purpose but not in the tab order: something jumping here from the failure listing has
        // to be able to land on a row that carries no control of its own.
        tabIndex = -1
        className = ClassName(rowClass(messages))
        // The layout's `label` (issue #586) shadows the schema title/humanized key; absent, `fieldLabel` decides.
        labelSpan(copy?.label ?: fieldLabel(name, prop, opts), required)
        // A `filled` default not yet accepted wears a "Suggested" chip on the container, not inside the control,
        // so the mark reads the same across text / select / checkbox / date (issue #710).
        if (prefill?.mode == SLDM.filled && prefill.suggested) {
            span {
                className = ClassName("field-suggested")
                +"Suggested"
            }
        }
        rowContent()
    }
    // Likewise the layout's `description` shadows the schema's; absent, the schema's shows as before.
    (copy?.description ?: prop.description)?.let { desc(it) }
    // Stated before the field is filled in, not discovered by being rejected. The outline shows the same
    // thing for an output type; this is the input side, which is the one someone is about to type into. The
    // layout's `hint` (issue #587), already resolved against the field's bounds, shadows the derived range.
    boundHint(prop.valueType, copy?.hint)
    fieldErrors(path, messages) { f -> layoutErrorMessage(copy, prop, name, value, f) }
}

/**
 * Marks a control as invalid and points it at the block holding its messages, so the state is announced, and
 * the reason read out rather than only being visible as a color. A no-op when [describedBy] is null, which
 * is how a field with nothing wrong says so.
 *
 * Set through `asDynamic` because these are plain HTML attributes that antd's components forward to the
 * control they wrap, and their Kotlin external interfaces do not declare them — the same route the "add" and
 * "remove" buttons already take for `aria-label`.
 *
 * That forwarding was checked rather than assumed, against a running instance: `Input`, `Select`,
 * `DatePicker` and `Checkbox` each land both attributes on their inner `<input>`, and the plain file input
 * takes them directly. A widget added later should be checked the same way — a silently dropped
 * `aria-describedby` looks identical to a working one.
 */
internal fun markInvalid(props: dynamic, describedBy: String?) {
    if (describedBy == null) return
    props["aria-invalid"] = true
    props["aria-describedby"] = describedBy
}

/** The row's class, marked invalid when the field carries a failure, so the label and control can be styled. */
private fun rowClass(messages: List<SchFailure>): String = if (messages.isEmpty()) "row" else "row field-invalid"

/**
 * The failures for one field, printed beneath it. The path is deliberately *not* repeated here — the message
 * is already sitting on the field it is about, and the path's job (saying which field) is done by position.
 * The listing at the bottom of the page keeps the path, since that surface has no field to sit next to.
 */
private fun ChildrenBuilder.fieldErrors(
    path: String,
    messages: List<SchFailure>,
    // The layout's error override for a failure (issue #588), or null to fall through. Default: no override,
    // which is every caller but the one field frame that carries a layout.
    override: (SchFailure) -> String? = { null },
) {
    if (messages.isEmpty()) return
    // One block per field rather than loose paragraphs, so the control can point `aria-describedby` at all of
    // its messages with a single id.
    div {
        id = ElementId(fieldErrorsId(path))
        messages.forEach { f ->
            p {
                className = ClassName("field-error")
                // The layout's per-code override wins when present, shown alone (its author decides whether to
                // name the choices, via `${'$'}{options}`); else the schema's own `g-errors` wording, and *only*
                // that -- someone filling in a form does not need the JSON type of what they got wrong. The
                // listing at the foot of the page keeps both, because that surface documents the wire (#202, #588).
                +(override(f) ?: "${f.userMessage ?: f.message}${choicesSuffix(f)}")
            }
        }
    }
}

/**
 * The "Valid options: …" sentence listing an invalid-option failure's choices; empty for every other failure.
 * Its own sentence rather than a trailing parenthetical, because the message it follows now ends in a period
 * and a lowercase "(valid: …)" stranded after one reads as an afterthought.
 *
 * Each choice shows its **value**, which is what actually goes in the payload — this surface documents the
 * wire, the same reason the endpoint form labels fields with their key rather than a `title`. The label is
 * appended only when it says something the value does not, so a labeled choice list is not reduced to bare
 * codes, and an unlabeled one is not padded with a repeat of itself.
 */
fun choicesSuffix(f: SchFailure): String {
    val opts = f.options ?: return ""
    return " Valid options: ${opts.joinToString(", ") { if (it.label == it.value) it.value else "${it.value} — ${it.label}" }}."
}

/**
 * An object type with a shape to lay out, as opposed to a free-form map.
 *
 * A discriminated union counts, and has to: its fields live on the branch rather than on the union, so it
 * declares no `properties` of its own and would otherwise look exactly like a free-form map — and be handed to
 * the raw-JSON editor, which is the one thing a union must not be edited as. Both places that ask this
 * question (a nested field and an array's element type) need the same answer, which is why it is asked here.
 */
internal fun isStructuredObject(vt: SchType): Boolean =
    vt.variants != null || (vt.jsonType == SCT.kObject && vt.properties.isNotEmpty())

/** The element type when [vt] is a list *of objects*, else null (a list of scalars stays a text widget). */
private fun objectElementType(vt: SchType): SchType? =
    if (vt.jsonType == SCT.array) vt.itemType?.takeIf { isStructuredObject(it) } else null

/**
 * A nested object field. Expansion follows the **data**, not the schema, once the field is optional or its type
 * has already been seen on this path — which is what lets a self-referential type (`TreeNode.parent`) be built
 * up one level at a time instead of showing a dead recursion marker, and what terminates it: a branch exists
 * only because someone added it.
 *
 * A *required* object still expands unmodified — it has to be filled in either way, so hiding it behind a
 * click would be friction with no gain.
 */
private fun ChildrenBuilder.renderNestedObject(
    name: String,
    prop: SchProperty,
    required: Boolean,
    value: Any?,
    vt: SchType,
    seen: Set<String>,
    editable: Boolean,
    path: String,
    errors: FieldErrors,
    emit: (Any?) -> Unit,
    omit: () -> Unit,
    opts: FormOpts,
    copy: LayoutCopy? = null,
    // Fields to leave out of this object (issue #642): its primary-key fields, promoted to the parent level.
    hideFields: Set<String> = emptySet(),
) {
    val typeName = vt.name
    val recursive = typeName != null && typeName in seen
    val present = value is Map<*, *>
    val dataDriven = recursive || !required
    val messages = errors.messagesAt(path)

    fieldFrame(name, prop, required, path, messages, opts, copy, value = value) {
        if (dataDriven && editable) {
            // Adding or removing the whole branch invalidates anything reported inside it, which is why the
            // edit is noted against this field rather than against whatever it contained.
            if (present) {
                removeControl(name) { errors.noteEdit(path); omit() }
            } else {
                addControl(name) { errors.noteEdit(path); emit(emptyMap<String, Any?>()) }
            }
        }
    }
    if (dataDriven && !present) {
        // Nothing there: awaiting an Add, or simply absent. Expanding it anyway would show a structure the
        // data does not have -- empty lat/lon for a contact with no location -- which reads as present-but-blank.
        if (!editable) {
            p {
                className = ClassName("type-hint")
                +"(none)"
            }
        }
        return
    }

    div {
        className = ClassName("nested")
        val childSeen = if (typeName != null) seen + typeName else seen
        renderObject(vt, value.toJsonMapOrEmpty(), childSeen, editable, path, errors, { newSub -> emit(newSub) }, opts, hideFields)
    }
}

/**
 * A list-of-objects field: each element is its own indented sub-form under an "i" header (the index
 * convention the response renderer already uses), with the "add" control after the last one, where an "append"
 * belongs. Removing the final element drops the whole field unless it is required, so an optional list does
 * not linger as an empty array in the payload.
 */
private fun ChildrenBuilder.renderObjectList(
    name: String,
    prop: SchProperty,
    required: Boolean,
    value: Any?,
    elementType: SchType,
    seen: Set<String>,
    editable: Boolean,
    path: String,
    errors: FieldErrors,
    emit: (Any?) -> Unit,
    omit: () -> Unit,
    opts: FormOpts,
    copy: LayoutCopy? = null,
) {
    val elements = value.toJsonListOrEmpty()
    val messages = errors.messagesAt(path)
    fieldFrame(name, prop, required, path, messages, opts, copy, value = value)

    val typeName = elementType.name
    val childSeen = if (typeName != null) seen + typeName else seen
    elements.forEachIndexed { i, element ->
        val elementPath = indexPath(path, i)
        // A failure against the element *itself* rather than one of its fields -- an element that is not an
        // object at all, say. Its fields each have a row; until now the element did not, so this was the one
        // failure inside a list with nowhere of its own to appear.
        val elementMessages = errors.messagesAt(elementPath)
        // A keyed element (issue #487) reads by its primary-key value -- "[i] Yearly — 2024" -- so several
        // entries of one trait are told apart at a glance; an unkeyed list keeps its bare index. Computed once
        // so the row's header and its remove button's label name the same thing.
        val header = elementRowHeader(elementType, element.toJsonMapOrEmpty(), i, opts.friendly, opts.fieldLayouts)
        div {
            className = ClassName("nested")
            div {
                id = ElementId(fieldRowId(elementPath))
                tabIndex = -1
                className = ClassName(rowClass(elementMessages))
                span {
                    className = ClassName("type-hint")
                    +header
                }
                // What the page knows about this element (issue #857) -- a locked section -- said on the element.
                opts.elementNote(elementPath, element.toJsonMapOrEmpty())?.let { note ->
                    span {
                        className = ClassName("element-note")
                        +note
                    }
                }
                if (editable) {
                    removeControl("$name $header") {
                        // Noted against the list, not the element: removing re-indexes everything after it, so
                        // a failure held against element 2 would end up pointing at what used to be element 3.
                        errors.noteEdit(path)
                        val rest = elements.filterIndexed { j, _ -> j != i }
                        if (rest.isEmpty() && !required) omit() else emit(rest)
                    }
                }
            }
            fieldErrors(elementPath, elementMessages)
            renderObject(elementType, element.toJsonMapOrEmpty(), childSeen, editable, elementPath, errors, { newElement ->
                emit(elements.mapIndexed { j, old -> if (j == i) newElement else old })
            }, opts)
        }
    }
    if (editable) {
        div {
            className = ClassName("row")
            addControl(name) {
                errors.noteEdit(path)
                emit(elements + listOf<Any?>(mapOf<String, Any?>()))
            }
        }
    } else if (elements.isEmpty()) {
        p {
            className = ClassName("type-hint")
            +"(none)"
        }
    }
}

/**
 * A list of scalars — a growing column of single-value widgets, each with its own remove and an "add" at the
 * end. It emits a real list, which is what an array field's type actually requires.
 *
 * The single comma-separated text box this replaces emitted a *String*, and an array type does not coerce from
 * one (`allowCoerce` defaults on only for numeric and date formats), so every entry failed validation as a
 * plain type mismatch — the field could be described but never filled in.
 */
private fun ChildrenBuilder.renderScalarList(
    name: String,
    prop: SchProperty,
    required: Boolean,
    value: Any?,
    elementType: SchType?,
    path: String,
    errors: FieldErrors,
    emit: (Any?) -> Unit,
    omit: () -> Unit,
    opts: FormOpts,
    copy: LayoutCopy? = null,
) {
    val elements = value.toJsonListOrEmpty()
    val messages = errors.messagesAt(path)
    fieldFrame(name, prop, required, path, messages, opts, copy, value = value)

    elements.forEachIndexed { i, element ->
        val elementPath = indexPath(path, i)
        val elementMessages = errors.messagesAt(elementPath)
        fun replace(newValue: Any?) {
            errors.noteEdit(elementPath)
            emit(elements.mapIndexed { j, old -> if (j == i) newValue else old })
        }
        div {
            id = ElementId(fieldRowId(elementPath))
            tabIndex = -1
            className = ClassName("${rowClass(elementMessages)} nested")
            // An untyped array declares nothing about its elements, so there is no widget to dispatch on:
            // a plain text box, whose value the validator leaves alone for want of an item type.
            //
            // An element is a slot that exists, so `required = true`: there is no absent state to express
            // inside a list (removing an element is the remove control's job), which is what a boolean
            // element asks about (issue #261).
            if (elementType != null) {
                widget(
                    elementType, element, required = true, editable = true, opts = opts,
                    zoneNoteId = fieldZoneNoteId(elementPath), commit = { errors.noteCommit(elementPath) },
                ) { replace(it) }
            } else {
                Input {
                    this.value = displayValue(element)
                    placeholder = "value"
                    onChange = { e -> replace(e.target.value as String) }
                }
            }
            removeControl("$name $i") {
                // Against the list, since removing re-indexes what follows (see renderObjectList).
                errors.noteEdit(path)
                val rest = elements.filterIndexed { j, _ -> j != i }
                if (rest.isEmpty() && !required) omit() else emit(rest)
            }
        }
        fieldErrors(elementPath, elementMessages)
    }
    div {
        className = ClassName("row")
        addControl(name) {
            errors.noteEdit(path)
            emit(elements + listOf<Any?>(""))
        }
    }
}

/**
 * The "add" affordance. It carries only the verb: it sits inside the block of the field it adds to, so position
 * supplies the noun — which also keeps the engine generic, since deriving a singular noun from a field name
 * ("contacts" -> "contact") is a guess that eventually produces nonsense. [what] names the field for assistive
 * technology, where that surrounding context is not available.
 */
private fun ChildrenBuilder.addControl(what: String, onAdd: () -> Unit) {
    Button {
        type = "link"
        size = "small"
        onClick = onAdd
        asDynamic()["aria-label"] = "Add $what"
        +"+ Add"
    }
}

private fun ChildrenBuilder.removeControl(what: String, onRemove: () -> Unit) {
    Button {
        type = "link"
        size = "small"
        danger = true
        onClick = onRemove
        asDynamic()["aria-label"] = "Remove $what"
        +"✕"
    }
}

/**
 * A supplied default's per-field affordance (issue #710), beside its control: for an unfilled `offer` field, a
 * **"Use *value*"** link that [apply]es the offered value as if typed; for a touched `filled` field, a **reset**
 * link that restores the suggestion. Draws nothing when there is no default here, the form is read-only, or an
 * offer has already been applied (its value is now present, so the field is an ordinary one). The `aria-label`
 * names the value, since "Use" alone does not carry it for a screen reader.
 */
private fun ChildrenBuilder.prefillControls(
    prefill: FieldPrefill?,
    value: Any?,
    editable: Boolean,
    apply: (Any?) -> Unit,
) {
    if (prefill == null || !editable) return
    when (prefill.mode) {
        SLDM.offer -> if (isBlankValue(value)) {
            Button {
                type = "link"
                size = "small"
                onClick = { apply(prefill.offeredValue) }
                asDynamic()["aria-label"] = "Use ${displayValue(prefill.offeredValue)}"
                +"Use ${displayValue(prefill.offeredValue)}"
            }
        }
        SLDM.filled -> prefill.onReset?.let { reset ->
            Button {
                type = "link"
                size = "small"
                onClick = { reset() }
                +"Reset to suggested"
            }
        }
    }
}

/**
 * Read-only presentation of a field: its value as text (nothing when absent) followed by the field's type
 * named in words. No form control — this is a value being shown, not an input. On a form people fill in
 * ([FormOpts.friendly]) the value reads as [friendlyValueText] says, with no type (issue #1438).
 */
internal fun ChildrenBuilder.readOnlyValue(
    vt: SchType, value: Any?, presentation: String? = vt.presentation, opts: FormOpts = FormOpts(),
) {
    // A structured array -- its elements are objects with declared fields -- reads far better as a nested table
    // (a sub-table inside the cell) than as raw JSON (issue #540): this is what turns a database-tables row's
    // `columns`/`indexes` into readable sub-tables rather than a JSON blob. A free-form array (no element
    // fields) still falls to the JSON view below.
    val elementType = vt.itemType
    if (vt.jsonType == SCT.array && elementType != null && elementType.properties.isNotEmpty() && value is List<*>) {
        schemaTable(elementType, value, opts)
        return
    }
    // A JSON structure (a generic object, or an array with structured elements) reads far better as pretty
    // JSON than a flattened toString; the kernel's JsonUtil formats it (indented, non-compact by default).
    if (value is Map<*, *> || (value is List<*> && value.any { it is Map<*, *> || it is List<*> })) {
        pre {
            className = ClassName("code json-value")
            +value.toJsonStr()
        }
        return
    }
    // A read-only scalar array whose *items* carry a hint (issue #540) renders each element with that hint -- a
    // row of status chips, a column of identifiers -- rather than the comma-joined line below, which would drop
    // it. An unhinted list still reads better joined.
    val itemPresentation = vt.itemType?.presentation
    if (vt.jsonType == SCT.array && itemPresentation != null && value is List<*>) {
        if (value.isEmpty()) {
            span { className = ClassName("field-type"); +"(empty list)" }
            return
        }
        span {
            className = ClassName("field-value op-list")
            for (element in value) readOnlyValue(vt.itemType!!, element, itemPresentation, opts)
        }
        return
    }
    val text = displayValue(value)
    // Presentation hints (issue #540), read-only only: a verdict shows as a coloured chip, an identifier as
    // monospace -- and both drop the "(type)" annotation, which documents the wire for the catalog outline but
    // is noise on a value a person is reading. An unknown hint falls through to ordinary text.
    when (presentation) {
        PRES.status -> {
            if (text.isNotEmpty()) statusChip(text)
            return
        }
        PRES.identifier -> {
            if (text.isNotEmpty()) span { className = ClassName("field-value op-identifier"); +text }
            return
        }
    }
    val shown = if (opts.friendly) friendlyValueText(vt, value) else text
    if (shown.isNotEmpty()) {
        span {
            className = ClassName("field-value")
            +shown
        }
    }
    if (!opts.friendly) {
        span {
            className = ClassName("field-type")
            +"(${typeWord(vt)})"
        }
    }
}

/** A [PSTAT] verdict as a coloured chip. An unrecognized value still shows, in the neutral (`info`) colour, so
 *  a status the frontend has not learned yet reads as text rather than vanishing. */
private fun ChildrenBuilder.statusChip(value: String) {
    val known = value in PSTAT.all
    span {
        className = ClassName("op-status " + if (known) value else PSTAT.info)
        +value
    }
}

/**
 * A read-only array rendered as a table (issue #540): the field frame (its label + description) above the
 * shared [schemaTable]. Used for a `presentation: table` array inside a form's read-only view.
 */
private fun ChildrenBuilder.renderTable(
    name: String,
    prop: SchProperty,
    required: Boolean,
    value: Any?,
    elementType: SchType,
    path: String,
    errors: FieldErrors,
    opts: FormOpts,
    copy: LayoutCopy? = null,
) {
    fieldFrame(name, prop, required, path, errors.messagesAt(path), opts, copy, value = value)
    schemaTable(elementType, value.toJsonListOrEmpty(), opts)
}

/**
 * The table markup for an array of [elementType] (issue #540): its properties are the columns, [elements] the
 * rows, and each cell is the ordinary [readOnlyValue] -- so a per-field hint (a `status` chip, an `identifier`
 * monospace) applies inside a cell exactly as it would anywhere else. Package-visible so an operator page can
 * render a bare result list directly, with its own heading rather than a field label.
 */
fun ChildrenBuilder.schemaTable(elementType: SchType, elements: List<Any?>, opts: FormOpts = FormOpts()) {
    if (elements.isEmpty()) {
        p { className = ClassName("type-hint"); +"(none)" }
        return
    }
    // Friendly mode drops a derived column and labels the rest by title -- exactly what the field form does --
    // so a table inside a friendly form matches its surroundings; the catalog (default opts) keeps every
    // column and labels by key, because it documents the wire.
    val shown = elementType.properties.values.filterNot { opts.friendly && it.valueType.derived }
    // A column marked `presentation: detail` (issue #540) is an array of objects that reads better as its own
    // sub-table beneath the row than crammed into an inline cell -- master-detail, for a heavy nested array
    // like a database table's `columns`. It only qualifies when it really is a structured array; otherwise it
    // stays an ordinary inline column.
    fun isDetail(col: SchProperty): Boolean =
        (col.presentation ?: col.valueType.presentation) == PRES.detail &&
            col.valueType.itemType?.properties?.isNotEmpty() == true
    val detailCols = shown.filter { isDetail(it) }
    val inlineCols = shown.filterNot { isDetail(it) }
    // A wide table (many columns, or a JSON/sub-table cell) scrolls inside this box rather than pushing past
    // the card and out of the window (issue #540).
    div {
        className = ClassName("op-table-scroll")
        table {
            className = ClassName("op-table")
            thead {
                tr { for (col in inlineCols) th { +fieldLabel(col.name, col, opts) } }
            }
            tbody {
                for (element in elements) {
                    val row = element.toJsonMapOrEmpty()
                    tr {
                        for (col in inlineCols) td {
                            // A per-site column hint wins over one on the column's (shared) target type.
                            readOnlyValue(col.valueType, row[col.name], col.presentation ?: col.valueType.presentation, opts)
                        }
                    }
                    // The detail arrays for this element, each a labelled sub-table on a full-width row under it.
                    if (detailCols.isNotEmpty()) {
                        tr {
                            className = ClassName("op-detail-row")
                            td {
                                asDynamic()["colSpan"] = inlineCols.size.coerceAtLeast(1)
                                for (col in detailCols) {
                                    div {
                                        className = ClassName("op-detail")
                                        div { className = ClassName("op-detail-label"); +fieldLabel(col.name, col, opts) }
                                        schemaTable(col.valueType.itemType!!, row[col.name].toJsonListOrEmpty(), opts)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

/** How many maps deep [typeWord] names a map's values ("map of map of string") before it says only "map". */
internal const val maxTypeWordDepth = 3

/** The field's type named in words, e.g. "string", "boolean", "date", "choice", "list". Internal (not private)
 *  so `ControlKindTest` can pin the choice-vs-open-choice reading it shares with [controlKind] (issue #781). */
internal fun typeWord(vt: SchType, depth: Int = 0): String = when {
    vt.jsonType == SCT.string && isBinaryFormat(vt.format) -> "file"
    // "open choice" rather than "choice": the word has to carry that the list is not the whole of what is
    // allowed, or the outline documents a constraint the endpoint does not have.
    vt.options != null && vt.openOptions -> "open choice"
    vt.options != null -> "choice"
    vt.jsonType == SCT.array && vt.itemType?.options != null && vt.itemType?.openOptions == true -> "open choices"
    vt.jsonType == SCT.array && vt.itemType?.options != null -> "choices"
    vt.jsonType == SCT.array -> "list"
    vt.jsonType == SCT.string && isDateFormat(vt.format) -> vt.format ?: SCT.string
    vt.jsonType == SCT.boolean -> "boolean"
    vt.jsonType == SCT.integer -> "integer"
    vt.jsonType == SCT.number -> "number"
    vt.jsonType == SCT.string -> "string"
    // A map (issue #1055): free keys, each a value of one kind -- which is what a reader needs to know of it. An
    // object that also declares fields is named for those, and its outline shows them. A map's values may be the
    // map itself, by reference, so the naming stops a few levels down.
    vt.properties.isEmpty() && vt.additionalValueType != null ->
        if (depth >= maxTypeWordDepth) "map" else "map of ${typeWord(vt.additionalValueType!!, depth + 1)}"
    else -> vt.jsonType ?: "value"
}

/** The field name plus a red `*` when required. */
internal fun ChildrenBuilder.labelSpan(name: String, required: Boolean) {
    span {
        className = ClassName("field-label")
        +name
        if (required) {
            span {
                className = ClassName("field-required")
                +" *"
            }
        }
    }
}

/**
 * A field's description, beneath it: inline Markdown (issue #1136), so it may emphasize, quote code, or link -- a
 * link to a document opening it in the app, resolved as all form copy is ([copyLinkResolver]). A field's *label*
 * stays plain text: a link there would sit on what focuses the control, and a label also heads a table column.
 */
internal fun ChildrenBuilder.desc(text: String) {
    FieldDescription { this.text = text }
}

private external interface FieldDescriptionProps : Props {
    var text: String
}

/** [desc]'s component, so the description can read the shell's document links. */
private val FieldDescription = FC<FieldDescriptionProps> { props ->
    val copyLinks = useShellFacts().copyLinkResolver()
    p {
        className = ClassName("subtitle")
        MarkdownInline { source = props.text; linkResolver = copyLinks }
    }
}

/** A short label of the expected value shape, used as an empty text widget's placeholder. */
internal fun typeHint(vt: SchType): String = when (vt.jsonType) {
    SCT.string if isBinaryFormat(vt.format) -> "file"
    SCT.string if isDateFormat(vt.format) -> vt.format ?: SCT.string
    else -> vt.jsonType ?: "value"
}

/**
 * A value as a form people fill in shows it (issue #1438): a yes/no answer as Yes or No, as a listing's cells and a
 * report's say it, and a choice by its label, each of a list of them too. Anything else, and a choice the schema does
 * not list, reads as [displayValue] has it.
 */
internal fun friendlyValueText(vt: SchType, value: Any?): String = when (value) {
    is Boolean -> if (value) "Yes" else "No"
    is List<*> -> value.joinToString(", ") { element -> vt.itemType?.let { friendlyValueText(it, element) } ?: displayValue(element) }
    else -> vt.options?.firstOrNull { it.value == value?.toString() }?.label ?: displayValue(value)
}

/** Renders a value for display; null becomes empty. A list is shown comma-joined. */
internal fun displayValue(value: Any?): String = when (value) {
    null -> ""
    is List<*> -> value.joinToString(", ") { it?.toString() ?: "" }
    else -> value.toString()
}

/** Converts [SchOption]s to the `{ label, value }` JS objects antd's Select `options` prop expects. */
internal fun optionsToJs(options: List<SchOption>): Array<dynamic> = options.map { opt ->
    val obj: dynamic = js("({})")
    obj.label = opt.label
    obj.value = opt.value
    obj
}.toTypedArray()

/** Converts a JS array (antd multi-select value) into a Kotlin list the kernel validator accepts. */
internal fun jsToList(v: dynamic): List<Any?> {
    if (v == null) return emptyList()
    val n = v.length as? Int ?: return emptyList()
    val out = ArrayList<Any?>(n)
    for (i in 0 until n) out.add(v[i])
    return out
}



/**
 * A field's declared bounds, stated the way the outline already states a choice field's options — the range
 * is part of what the shape *is*, and reading it here beats discovering it by being rejected (issue #203).
 * Silent when the field declares neither end.
 */
internal fun ChildrenBuilder.boundHint(vt: SchType, override: String? = null) {
    // A layout `hint` (issue #587) replaces the derived range wholesale -- it was resolved against this field's
    // `${'$'}{min}` / `${'$'}{max}`, so it says what the author wanted in place of "range: X to Y". A blank override is
    // treated as "no hint" so a layout can suppress the derived one by setting it empty.
    if (override != null) {
        if (override.isNotBlank()) {
            p {
                className = ClassName("type-hint")
                +override
            }
        }
        return
    }
    val text = boundHintText(vt) ?: return
    p {
        className = ClassName("type-hint")
        +text
    }
}

/**
 * The hint a field's declared bounds give it -- "range: 1 to 200", "characters: 3 or more" -- or null when it declares
 * neither end. What a form shows beneath the field when no layout `hint` replaces it. Pure.
 */
fun boundHintText(vt: SchType): String? {
    val min = vt.minBound
    val max = vt.maxBound
    if (min == null && max == null) return null
    val what = when (vt.jsonType) {
        SCT.string -> "characters"
        SCT.array -> "items"
        SCT.kObject -> "properties"
        else -> null
    }
    // An exclusive end (issue #823) does not admit the bound itself, so it is said differently.
    val range = when {
        min != null && max != null ->
            "${min.fmtD()}${if (vt.minExclusive) " (exclusive)" else ""} to " +
                "${max.fmtD()}${if (vt.maxExclusive) " (exclusive)" else ""}"
        min != null -> if (vt.minExclusive) "more than ${min.fmtD()}" else "${min.fmtD()} or more"
        else -> if (vt.maxExclusive) "less than ${max!!.fmtD()}" else "${max!!.fmtD()} or less"
    }
    return if (what == null) "range: $range" else "$what: $range"
}
